import React, { useReducer, useRef } from "react";
import {
  Body,
  Button,
  Checkbox,
  Input,
  Line,
  useChameleonTheme,
} from "@petsapp/chameleon";
import useAuth from "@petsapp/use-auth";
import { useState } from "react";
import styled from "styled-components";
import { WellnessPlan } from "../wellness-plans/types";

interface State {
  fields: {
    email?: string;
    password?: string;
    firstName?: string;
    lastName?: string;
  };
  error?: string;
  acceptedTerms: boolean;
  canRegister: boolean;
}

const ButtonContainer = styled.div`
  > label {
    margin-bottom: 24px;
  }
`;

function hasAllFields(fields: State["fields"]) {
  return (
    fields.email != null &&
    fields.password != null &&
    fields.firstName != null &&
    fields.lastName != null
  );
}

function reducer(state: State, action: any) {
  if (action.type === "set-error") {
    return {
      ...state,
      error: action.description,
    };
  }
  if (action.type === "update-field") {
    const newState = {
      ...state,
      fields: {
        ...state.fields,
        [action.data.fieldName]: action.data.value,
      },
    };

    if (state.acceptedTerms && hasAllFields(newState.fields)) {
      newState.canRegister = true;
    }

    return newState;
  }

  if (action.type === "toggle-accepted-terms") {
    const newState = { ...state, acceptedTerms: !state.acceptedTerms };
    if (newState.acceptedTerms && hasAllFields(newState.fields)) {
      newState.canRegister = true;
    }
    return newState;
  }

  return state;
}

export function RegisterForm({ plan }: { plan: WellnessPlan | null }) {
  const auth = useAuth();
  const errorRef = useRef<HTMLDivElement>(null);
  const theme = useChameleonTheme() as Record<any, any>;

  const [loading, setLoading] = useState(false);
  const [state, dispatch] = useReducer(reducer, {
    canRegister: false,
    acceptedTerms: false,
    fields: {},
  });

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    dispatch({ type: "set-error", description: null });

    if (state.canRegister && hasAllFields(state.fields)) {
      setLoading(true);
      try {
        await auth.register(
          // @ts-ignore
          state.fields,
        );
      } catch (e) {
        /**
         * Could not reproduce other error codes that 'invalid_signup' and 'invalid_password'
         * Tried to cover them anyway to make sure people get proper message
         * https://auth0.com/docs/libraries/common-auth0-library-authentication-errors
         */
        let description: string | string[] =
          "Error creating account, please check your details and try again.";

        if (e.code === "invalid_password") {
          description = ["Invalid password:"]
            .concat(e.policy.split("\n"))
            .concat("* we check against common used passwords in data leaks");
        }

        if (
          state.fields.email &&
          (e.code === "invalid_signup" ||
            e.code === "user_exists" ||
            e.code === "username_exists")
        ) {
          description = `Error creating account, are you sure you're not already registered with ${state.fields.email}? Click "Sign in" and try to use this email.`;
        }

        if (e.code === "password_dictionary_error") {
          description =
            "Error creating account, the choosen password is too common";
        }

        if (e.code === "password_no_user_info_error") {
          description =
            "Error creating account, the chosen password is based on user information";
        }

        if (e.code === "password_strength_error") {
          description =
            "Error creating account, the chosen password is too weak";
        }

        dispatch({ type: "set-error", description });

        if (errorRef.current) {
          const el = errorRef.current;
          el.scrollIntoView();
        }
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <React.Fragment>
      <form onSubmit={submit}>
        <Input
          id="first-name"
          onChange={(e) =>
            dispatch({
              type: "update-field",
              data: { fieldName: "firstName", value: e.currentTarget.value },
            })
          }
          label="First name"
        />
        <Input
          id="last-name"
          onChange={(e) =>
            dispatch({
              type: "update-field",
              data: { fieldName: "lastName", value: e.currentTarget.value },
            })
          }
          label="Last name"
        />
        <Input
          id="email"
          onChange={(e) =>
            dispatch({
              type: "update-field",
              data: { fieldName: "email", value: e.currentTarget.value },
            })
          }
          label="Email"
          type="email"
        />
        <Input
          id="password"
          onChange={(e) =>
            dispatch({
              type: "update-field",
              data: { fieldName: "password", value: e.currentTarget.value },
            })
          }
          label="Password"
          type="password"
        />
        <Line />
        <Body>
          Before you use our app please confirm you have read and agree to both
          our{" "}
          <a
            href="https://petsapp.com/terms-and-conditions"
            rel="noreferrer"
            target="_blank"
          >
            Terms and Conditions
          </a>{" "}
          and{" "}
          <a
            href="https://petsapp.com/privacy-policy"
            rel="noreferrer"
            target="_blank"
          >
            Privacy Policy
          </a>{" "}
          by clicking on “I agree” below, including provisions relating to our
          recording of any appointments carried out via the app as summarised
          therein. If you don’t so agree, simply exit this app.
        </Body>
        <ButtonContainer>
          <Checkbox
            onChange={() => dispatch({ type: "toggle-accepted-terms" })}
            state={state.acceptedTerms ? "selected" : undefined}
            label="I agree"
          />
          <Button
            type="submit"
            state={
              loading ? "loading" : state.canRegister ? undefined : "disabled"
            }
            variant="primary"
          >
            Create account
          </Button>
        </ButtonContainer>
        {state.error && (
          <div ref={errorRef} style={{ marginTop: "0.75rem" }}>
            {(Array.isArray(state.error) ? state.error : [state.error]).map(
              (error) => (
                <Body
                  style={{
                    color: theme.chameleon.color.Red,
                    marginBottom: "0.5rem",
                  }}
                >
                  {error}
                </Body>
              ),
            )}
          </div>
        )}
      </form>
    </React.Fragment>
  );
}
